import React from "react";
import { AlertTriangle, Eye, Upload, X } from "lucide-react";

/**
 * Shown when an employee tries to upload a document type they already have.
 * Lets them preview the existing file and decide whether to replace it.
 */
const DuplicateDocumentModal = ({
  open,
  onClose,
  onConfirm,           // proceed with upload (replace)
  existingDoc,         // { id, type, filePath, created_at }
  newFile,             // File object being uploaded
  selectedType,
  language = "en",
  theme = "light",
  onPreviewExisting,   // () => opens existing doc preview
}) => {
  if (!open) return null;

  const isDark = theme === "dark";

  const overlay = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.6)",
    backdropFilter: "blur(4px)",
    zIndex: 11000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  };

  const card = {
    background: isDark
      ? "linear-gradient(135deg,#0f172a 0%,#1a2a3a 100%)"
      : "linear-gradient(135deg,#fff 0%,#f8fafc 100%)",
    border: isDark
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid rgba(148,163,184,0.2)",
    borderRadius: 16,
    padding: "28px 28px 24px",
    width: "100%",
    maxWidth: 500,
    color: isDark ? "#e2e8f0" : "#0f172a",
    boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
    position: "relative",
  };

  const section = {
    borderRadius: 10,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    marginBottom: 12,
  };

  const tag = (color) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
    background: `${color}22`,
    color,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
  });

  const btn = (bg, color, border) => ({
    flex: 1,
    padding: "11px 0",
    borderRadius: 10,
    border: border || "none",
    background: bg,
    color,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    transition: "opacity 200ms",
  });

  const isAr = language === "ar";

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            [isAr ? "left" : "right"]: 14,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: isDark ? "#94a3b8" : "#64748b",
            padding: 4,
          }}
        >
          <X size={18} />
        </button>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "rgba(245,158,11,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <AlertTriangle size={20} color="#f59e0b" />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>
              {isAr ? "مستند موجود بالفعل" : "Document Already Exists"}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: isDark ? "#94a3b8" : "#64748b", marginTop: 2 }}>
              {isAr
                ? `لديك بالفعل مستند من نوع "${selectedType}". هل تريد استبداله؟`
                : `You already have a "${selectedType}" document. Replace it?`}
            </p>
          </div>
        </div>

        {/* Existing document */}
        <div
          style={{
            ...section,
            background: isDark ? "rgba(239,68,68,0.08)" : "rgba(239,68,68,0.05)",
            border: "1px solid rgba(239,68,68,0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={tag("#ef4444")}>{isAr ? "الحالي" : "Current"}</span>
            <button
              onClick={onPreviewExisting}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "#ef4444",
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Eye size={13} />
              {isAr ? "معاينة" : "Preview"}
            </button>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {existingDoc?.type || selectedType}
          </span>
          {existingDoc?.created_at && (
            <span style={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
              {isAr ? "تم الرفع:" : "Uploaded:"}{" "}
              {new Date(existingDoc.created_at).toLocaleDateString(
                isAr ? "ar-EG" : "en-US"
              )}
            </span>
          )}
        </div>

        {/* New document */}
        <div
          style={{
            ...section,
            background: isDark ? "rgba(59,130,246,0.08)" : "rgba(59,130,246,0.05)",
            border: "1px solid rgba(59,130,246,0.2)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={tag("#3b82f6")}>{isAr ? "جديد" : "New"}</span>
          </div>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{newFile?.name}</span>
          {newFile?.size && (
            <span style={{ fontSize: 11, color: isDark ? "#64748b" : "#94a3b8" }}>
              {(newFile.size / 1024 / 1024).toFixed(2)} MB
            </span>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button
            style={btn(
              isDark ? "rgba(30,41,59,0.8)" : "rgba(241,245,249,0.9)",
              isDark ? "#cbd5e1" : "#475569",
              isDark ? "1.5px solid rgba(255,255,255,0.15)" : "1.5px solid rgba(0,0,0,0.12)"
            )}
            onClick={onClose}
          >
            {isAr ? "إلغاء" : "Cancel"}
          </button>
          <button
            style={btn("linear-gradient(135deg,#2e7cba,#1d4ed8)", "#fff")}
            onClick={onConfirm}
          >
            <Upload size={14} />
            {isAr ? "استبدال المستند" : "Replace Document"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateDocumentModal;
